/**
 * SRES · Mobile Approvals Landing Page (Iter80 · Feb 2026).
 *
 * A slim, phone-optimised view of a single narrative paragraph with
 * prominent Approve / Reject actions. Purpose: CPs reviewing on
 * phones or tablets between site visits click a deep-link (typically
 * from the CPR PDF footnote) and land here without navigating the
 * full workspace shell.
 *
 * Route: /app/approve/:pid/:paragraphId
 *   Auth-gated via <ProtectedRoute> (redirects to /login?returnTo=… if
 *   the CP isn't signed in). Renders OUTSIDE the AppShell — no
 *   sidebar, no wizard chrome — so the 4-item sidebar rail rule
 *   remains unaffected on the desktop workspace routes.
 *
 * Payload contract mirrors FinalReviewStep exactly:
 *   POST /projects/{pid}/narratives/{paraId}/approve
 *     { role, signature, notes?, pin? }
 *   POST /projects/{pid}/narratives/{paraId}/reject
 *     { reason, notes? }
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, Signature, Lock, ArrowLeft, Warning,
} from "@phosphor-icons/react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import SignaturePad from "@/components/SignaturePad";


// ═══════════════════════════════════════════════════════════════════
export default function ApprovalsLandingPage() {
  const { pid, paragraphId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const padRef = useRef(null);

  // Paragraph fetch — we load the full list and pick the target because
  // the wizard's per-project narratives endpoint already ships the
  // signature progress and required roles alongside each paragraph.
  const paraQ = useQuery({
    queryKey: ["approval-para", pid, paragraphId],
    queryFn:  () => api.get(`/projects/${pid}/narratives`).then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      return rows.find(
        (p) => (p.paragraph_id || p.id) === paragraphId) || null;
    }),
    enabled: !!pid && !!paragraphId,
    staleTime: 5_000,
  });

  const matrixQ = useQuery({
    queryKey: ["approval-matrix", pid],
    queryFn:  () => api.get(`/projects/${pid}/signature-matrix`).then((r) => r.data),
    enabled: !!pid,
    staleTime: 30_000,
  });

  const para = paraQ.data;
  const requiredRoles = matrixQ.data?.required_roles || ["cp"];
  const dualSignoff   = requiredRoles.length > 1;
  const signedRoles   = para?.signed_roles || [];
  const alreadySigned = new Set(signedRoles);
  const isTerminal    = !!para && (
    ["cp_qp_approved", "rejected", "superseded"].includes(para.review_status)
      || para.locked);

  // ── Signature-pad state ─────────────────────────────────────────
  const [role,  setRole]  = useState("cp");
  const [mode,  setMode]  = useState("draw");   // draw | type
  const [typed, setTyped] = useState("");
  const [drawn, setDrawn] = useState("");
  const [notes, setNotes] = useState("");
  const [pin,   setPin]   = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason,     setReason]     = useState("");

  useEffect(() => {
    // First time the paragraph loads, default the role to the first
    // required role that hasn't been signed yet. Deliberately keyed
    // ONLY on paragraph_id — we do not want the role to jump back
    // when `alreadySigned` mutates mid-session (e.g. after the user
    // clicks Sign and `signed_roles` refetches with their signature).
    if (!para) return;
    if (dualSignoff) {
      const pending = requiredRoles.find((r) => !alreadySigned.has(r));
      if (pending) setRole(pending);
    } else {
      setRole(requiredRoles[0] || "cp");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fire once per paragraph arrival
  }, [para?.paragraph_id]);

  const signaturePayload = mode === "draw" ? drawn : typed.trim();
  const canSign = !isTerminal && !!signaturePayload && signaturePayload.length >= 4;

  // ── Mutations ────────────────────────────────────────────────────
  const approveMut = useMutation({
    mutationFn: (body) =>
      api.post(`/projects/${pid}/narratives/${paragraphId}/approve`, body)
              .then((r) => r.data),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["approval-para", pid, paragraphId] });
      if (resp?.fully_signed) {
        toast.success("Paragraph fully approved and signed.");
      } else {
        const missing = (resp?.missing_roles || []).map((r) => r.toUpperCase());
        toast.success(
          missing.length
            ? `Signed as ${resp.role?.toUpperCase()}. Awaiting ${missing.join("/")} co-signature.`
            : "Paragraph approved and signed.",
        );
      }
      padRef.current?.clear?.();
      setDrawn(""); setTyped(""); setNotes(""); setPin("");
    },
    onError: (e) => {
      toast.error(e?.response?.data?.message
                     || e?.response?.data?.detail
                     || "Approve failed.");
    },
  });

  const rejectMut = useMutation({
    mutationFn: (body) =>
      api.post(`/projects/${pid}/narratives/${paragraphId}/reject`, body)
              .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approval-para", pid, paragraphId] });
      toast.success("Paragraph rejected.");
      setRejectOpen(false);
      setReason("");
    },
    onError: (e) => {
      toast.error(e?.response?.data?.message
                     || "Reject failed.");
    },
  });

  const handleApprove = () => {
    if (!canSign) return;
    approveMut.mutate({
      role,
      signature: signaturePayload,
      notes: notes.trim() || undefined,
      pin:   pin.trim()   || undefined,
    });
  };

  const handleReject = () => {
    if (!reason.trim()) {
      toast.error("Please provide a rejection reason.");
      return;
    }
    rejectMut.mutate({ reason: reason.trim(), notes: notes.trim() || undefined });
  };

  // ══════════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════════
  if (paraQ.isLoading) {
    return (
      <div data-testid="approval-loading"
              className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm">
        Loading paragraph…
      </div>
    );
  }

  if (!para) {
    return (
      <div data-testid="approval-not-found"
              className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-50 p-6">
        <Warning size={40} className="text-amber-500" weight="fill" />
        <div className="text-slate-900 font-heading font-bold text-lg text-center">
          Paragraph not found
        </div>
        <div className="text-slate-500 text-[13px] text-center max-w-md">
          The paragraph you tried to open no longer exists on this project,
          or the deep-link URL is malformed.
        </div>
        <Button variant="outline"
                    onClick={() => navigate(`/app/projects/${pid}/wizard/final_review`)}
                    data-testid="approval-back-to-final-review">
          Back to Final Review
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="approvals-landing" className="min-h-screen bg-slate-50 flex flex-col">
      {/* Slim header — no sidebar chrome */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3"
                  data-testid="approval-header">
        <button
          type="button"
          onClick={() => navigate(`/app/projects/${pid}/wizard/final_review`)}
          className="text-slate-500 hover:text-brand-blue"
          data-testid="approval-back"
          aria-label="Back to Final Review"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">
            Sign narrative paragraph
          </div>
          <div className="text-[13.5px] font-heading font-bold text-slate-900 truncate">
            {para.chapter || "Uncategorised"}
          </div>
        </div>
        <StatusPill status={para.review_status} />
      </header>

      {/* Paragraph card */}
      <main className="flex-1 p-4 space-y-4 pb-32">
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                        data-testid="approval-paragraph-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">
              Paragraph v{para.version || 1} · {para.word_count || 0} words
            </div>
            {para.locked && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-600">
                <Lock size={10} /> Locked
              </span>
            )}
          </div>
          <div className="text-[13.5px] leading-relaxed text-slate-800 whitespace-pre-wrap"
                    data-testid="approval-paragraph-body">
            {para.body || para.text_body || para.text || para.paragraph
              || para.content || <span className="italic text-slate-400">No paragraph text.</span>}
          </div>

          {/* Signature progress dots */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2"
                    data-testid="approval-sig-progress">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500">
              Signatures
            </div>
            {requiredRoles.map((r) => {
              const done = alreadySigned.has(r);
              return (
                <span key={r}
                            data-testid={`approval-sig-${r}`}
                            data-signed={done ? "yes" : "no"}
                            className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                              done
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-slate-200 bg-slate-50 text-slate-500"
                            }`}>
                  {r.toUpperCase()} {done ? "✓" : "•"}
                </span>
              );
            })}
          </div>
        </section>

        {/* Sign block (hidden once terminal) */}
        {!isTerminal && !rejectOpen && (
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3"
                          data-testid="approval-sign-block">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
                  Signing as
                </div>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  data-testid="approval-role"
                  className="w-full h-10 text-[13.5px] border border-slate-200 rounded px-2 bg-white"
                >
                  {["cp", "qp"].map((r) => (
                    <option key={r} value={r}
                                disabled={dualSignoff && alreadySigned.has(r)}>
                      {r === "cp" ? "CP · Competent Person" : "QP · Qualified Person"}
                      {dualSignoff && alreadySigned.has(r) ? " · signed" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
                  PIN (optional)
                </div>
                <Input value={pin} onChange={(e) => setPin(e.target.value)}
                          placeholder="4-digit PIN" data-testid="approval-pin"
                          inputMode="numeric" className="h-10 text-[13.5px]" />
              </label>
            </div>

            {/* Signature */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-500">
                  Signature
                </div>
                <div className="flex-1" />
                <div className="inline-flex rounded border border-slate-200 overflow-hidden">
                  <button type="button" onClick={() => setMode("draw")}
                              data-testid="approval-mode-draw"
                              className={`px-2 py-1 text-[10.5px] uppercase tracking-widest ${
                                mode === "draw"
                                  ? "bg-brand-blue text-white"
                                  : "bg-white text-slate-500"
                              }`}>Draw</button>
                  <button type="button" onClick={() => setMode("type")}
                              data-testid="approval-mode-type"
                              className={`px-2 py-1 text-[10.5px] uppercase tracking-widest ${
                                mode === "type"
                                  ? "bg-brand-blue text-white"
                                  : "bg-white text-slate-500"
                              }`}>Type</button>
                </div>
              </div>
              {mode === "draw" ? (
                <SignaturePad ref={padRef} height={140}
                                    onChange={(v) => setDrawn(v)}
                                    data-testid="approval-sig-pad" />
              ) : (
                <Input value={typed} onChange={(e) => setTyped(e.target.value)}
                          placeholder="Type your signature (min 4 chars)"
                          data-testid="approval-sig-typed"
                          className="text-[15px]" />
              )}
            </div>

            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1">
                Notes (optional)
              </div>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                              rows={2} data-testid="approval-notes"
                              className="text-[13.5px]" />
            </div>
          </section>
        )}

        {/* Reject expanded block */}
        {!isTerminal && rejectOpen && (
          <section className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 shadow-sm space-y-3"
                          data-testid="approval-reject-block">
            <div className="text-[10.5px] uppercase tracking-widest text-rose-700 font-semibold">
              Reject this paragraph
            </div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                          placeholder="Provide a clear reason for rejection."
                          rows={3}
                          className="text-[13.5px]"
                          data-testid="approval-reject-reason" />
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                          placeholder="Optional notes to the author."
                          rows={2}
                          className="text-[13.5px]"
                          data-testid="approval-reject-notes" />
          </section>
        )}

        {isTerminal && (
          <section className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-[13px] text-emerald-800"
                          data-testid="approval-terminal-notice">
            <div className="font-heading font-bold mb-1">
              This paragraph is already {(para.review_status || "closed").replace(/_/g, " ")}.
            </div>
            <div className="text-[12px]">
              Return to the Final Review list to continue signing the next paragraph.
            </div>
          </section>
        )}
      </main>

      {/* Sticky footer with the primary actions */}
      {!isTerminal && (
        <footer
          data-testid="approval-footer"
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 flex items-center gap-2 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
        >
          {!rejectOpen ? (
            <>
              <Button
                variant="outline" size="lg"
                onClick={() => setRejectOpen(true)}
                data-testid="approval-open-reject"
                className="h-12 gap-1 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 text-[13.5px]"
              >
                <XCircle size={16} weight="fill" /> Reject
              </Button>
              <Button
                size="lg"
                onClick={handleApprove}
                disabled={!canSign || approveMut.isPending}
                data-testid="approval-submit"
                className="h-12 gap-1 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[13.5px] font-semibold disabled:opacity-50"
              >
                <Signature size={16} weight="fill" />
                {approveMut.isPending ? "Signing…" : "Approve & Sign"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="lg"
                          onClick={() => { setRejectOpen(false); setReason(""); }}
                          data-testid="approval-reject-cancel"
                          className="h-12 text-[13.5px]">
                Cancel
              </Button>
              <Button size="lg" onClick={handleReject}
                          disabled={!reason.trim() || rejectMut.isPending}
                          data-testid="approval-reject-submit"
                          className="h-12 gap-1 flex-1 bg-rose-600 hover:bg-rose-700 text-white text-[13.5px] font-semibold disabled:opacity-50">
                <XCircle size={16} weight="fill" />
                {rejectMut.isPending ? "Rejecting…" : "Confirm reject"}
              </Button>
            </>
          )}
        </footer>
      )}

      {isTerminal && (
        <footer
          data-testid="approval-footer-terminal"
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 flex items-center gap-2"
        >
          <Button size="lg" variant="outline"
                    onClick={() => navigate(`/app/projects/${pid}/wizard/final_review`)}
                    data-testid="approval-back-to-final-review"
                    className="h-12 flex-1 gap-1 text-[13.5px]">
            <ArrowLeft size={16} /> Back to Final Review
          </Button>
        </footer>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
function StatusPill({ status }) {
  const tones = {
    ai_generated:         "text-slate-500 bg-slate-100 border-slate-200",
    ai_edited:            "text-brand-blue bg-brand-blue/10 border-brand-blue/30",
    technically_approved: "text-brand-blue bg-brand-blue/10 border-brand-blue/30",
    cp_qp_approved:       "text-emerald-700 bg-emerald-50 border-emerald-200",
    rejected:             "text-rose-700 bg-rose-50 border-rose-200",
    superseded:           "text-slate-400 bg-slate-50 border-slate-200",
  };
  const t = tones[status] || "text-slate-500 bg-slate-100 border-slate-200";
  return (
    <span
      data-testid="approval-status-pill"
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${t}`}
    >
      {status === "cp_qp_approved" && <CheckCircle size={10} weight="fill" />}
      {(status || "unknown").replace(/_/g, " ")}
    </span>
  );
}
